import { FormEvent, useEffect, useMemo, useState } from "react";
import { Check, Copy, ExternalLink, X } from "lucide-react";
import { Link } from "react-router-dom";
import { signup, getTypes, type TypeOption } from "../../api/authEtablissement";
import { declareEtablissement } from "../../api/professional";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import CountryPhoneInput from "../../components/CountryPhoneInput";
import { showToast } from "../../components/ui/toastHotSonner";

const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

/** Bénin (+229) garde son format historique "01" + 8 chiffres ; les autres pays acceptent 6 à 12 chiffres. */
function isNationalNumberValid(dialCode: string, national: string): boolean {
  if (!national) return true; // optionnel
  if (dialCode === "229") return /^01\d{8}$/.test(national);
  return national.length >= 6 && national.length <= 12;
}

function groupByTwo(digits: string): string {
  return digits.replace(/(\d{2})(?=\d)/g, "$1 ");
}

type Step = "form" | "success";

type OfficializeResult = {
  etablissementId: string;
  nom: string;
  email: string;
};

export default function OfficialiserCabinetModal({
  nip,
  professionalNom,
  professionalPrenom,
  professionalEmail,
  onClose,
  onDone,
}: {
  nip: string;
  professionalNom?: string;
  professionalPrenom?: string;
  professionalEmail?: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [step, setStep] = useState<Step>("form");
  const [nom, setNom] = useState("");
  const [type, setType] = useState("");
  const [types, setTypes] = useState<TypeOption[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [adresse, setAdresse] = useState("");
  const [dialCode, setDialCode] = useState("229");
  const [nationalPhone, setNationalPhone] = useState("");
  const [numeroAgrement, setNumeroAgrement] = useState("");
  const [directeurNom, setDirecteurNom] = useState(professionalNom ?? "");
  const [directeurPrenom, setDirecteurPrenom] = useState(professionalPrenom ?? "");
  const [email, setEmail] = useState(professionalEmail ?? "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<OfficializeResult | null>(null);
  const [pendingEtablissementId, setPendingEtablissementId] = useState<string | null>(null);
  const [emailCopied, setEmailCopied] = useState(false);

  useEffect(() => {
    let mounted = true;
    getTypes().then((res) => {
      if (!mounted) return;
      setLoadingTypes(false);
      if (res.ok) setTypes(res.data);
    });
    return () => { mounted = false; };
  }, []);

  const phoneValid = useMemo(() => isNationalNumberValid(dialCode, nationalPhone), [dialCode, nationalPhone]);
  const telephone = useMemo(
    () => (nationalPhone ? `+${dialCode} ${groupByTwo(nationalPhone)}`.trim() : undefined),
    [dialCode, nationalPhone]
  );
  const emailValid = useMemo(() => emailRegex.test(email.trim()), [email]);
  const passwordStrong = useMemo(() => passwordRegex.test(password), [password]);
  const passwordsMatch = confirmPassword === password && confirmPassword.length > 0;

  const canSubmit =
    !submitting &&
    nom.trim() !== "" &&
    type !== "" &&
    adresse.trim() !== "" &&
    phoneValid &&
    emailValid &&
    passwordStrong &&
    passwordsMatch;

  const declareLiaison = async (etablissementId: string) => {
    const liaisonRes = await declareEtablissement(nip, { etablissementId });
    if (!liaisonRes.ok) {
      setPendingEtablissementId(etablissementId);
      setError(
        `Établissement créé, mais la déclaration d'exercice a échoué : ${liaisonRes.message} — réessayez ci-dessous.`
      );
      showToast(`La déclaration d'exercice a échoué : ${liaisonRes.message}`, "error");
      return false;
    }
    return true;
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    const signupRes = await signup({
      email: email.trim(),
      password,
      confirmPassword,
      type_utilisateur: type,
      profileData: {
        nom: nom.trim(),
        type,
        adresse: adresse.trim(),
        telephone,
        numero_agrement: numeroAgrement || undefined,
        directeur_nom: directeurNom.trim() || undefined,
        directeur_prenom: directeurPrenom.trim() || undefined,
      },
    });
    if (!signupRes.ok) {
      setSubmitting(false);
      showToast(signupRes.message || "Une erreur est survenue lors de l'inscription.", "error");
      return;
    }
    const etablissementId = signupRes.data.user?.etablissementId ?? signupRes.data.user?.id;
    if (!etablissementId) {
      setSubmitting(false);
      showToast(
        "Établissement créé, mais son identifiant n'a pas été renvoyé par le serveur — vérifiez « Mes établissements » dans quelques instants.",
        "error"
      );
      return;
    }
    const declared = await declareLiaison(etablissementId);
    setSubmitting(false);
    if (!declared) return;
    setResult({ etablissementId, nom: nom.trim(), email: email.trim() });
    setStep("success");
  };

  const retryDeclare = async () => {
    if (!pendingEtablissementId) return;
    setSubmitting(true);
    const declared = await declareLiaison(pendingEtablissementId);
    setSubmitting(false);
    if (!declared) return;
    setError(null);
    setResult({ etablissementId: pendingEtablissementId, nom: nom.trim(), email: email.trim() });
    setStep("success");
  };

  const handleCopyEmail = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.email);
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    } catch {
      /* Presse-papiers indisponible : l'email reste affiché à l'écran. */
    }
  };

  const handleClose = () => {
    onClose();
    if (result) onDone();
  };

  return (
    <div className="fixed inset-0 z-20" onClick={handleClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div className="absolute inset-0 flex items-center justify-center p-4 overflow-y-auto">
        <div
          className="bg-white rounded-[12px] p-6 w-full max-w-3xl shadow my-8 space-y-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-[#0B1F16]">
              {step === "form" ? "Officialiser mon établissement" : "Établissement créé"}
            </h3>
            <button type="button" onClick={handleClose} className="text-[#0B1F16]/60 hover:text-[#0B1F16]">
              <X size={18} />
            </button>
          </div>

          {step === "form" && (
            <>
              <p className="text-xs text-gray-500">
                Contrairement à votre cabinet instantané (actif immédiatement), ce nouvel établissement suit
                l'inscription établissement classique — mêmes champs, mêmes documents à soumettre ensuite,
                même délai de validation. Votre cabinet déjà actif et ses patients ne sont pas affectés.
              </p>
              <form className="space-y-4" onSubmit={onSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                  {/* Colonne gauche : identité de l'établissement */}
                  <div className="space-y-2">
                    <label className="text-sm">Nom de l'établissement</label>
                    <input
                      value={nom}
                      onChange={(e) => setNom(e.target.value.toUpperCase())}
                      placeholder="CABINET DR. AHOUNOU"
                      className="w-full h-11 rounded-[10px] border px-3 bg-white"
                      style={{ borderColor: "#E5E7EB", textTransform: "uppercase" }}
                    />
                  </div>

                  {/* Colonne droite : responsable */}
                  <div className="space-y-2">
                    <label className="text-sm">Nom du directeur</label>
                    <input
                      value={directeurNom}
                      onChange={(e) => setDirecteurNom(e.target.value.toUpperCase())}
                      className="w-full h-11 rounded-[10px] border px-3 bg-white"
                      style={{ borderColor: "#E5E7EB", textTransform: "uppercase" }}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm">Type de structure</label>
                    <Select value={type} onValueChange={setType} disabled={loadingTypes}>
                      <SelectTrigger className="w-full h-11 rounded-[10px] border px-3 bg-white" style={{ borderColor: "#E5E7EB" }}>
                        <SelectValue placeholder="Choisir…" />
                      </SelectTrigger>
                      <SelectContent>
                        {(types.length > 0
                          ? types.map((t) => ({
                              value: t.type ?? t.code ?? t.libelle ?? t.label ?? "",
                              label: t.libelle ?? t.label ?? t.code ?? t.id ?? "",
                            }))
                          : [
                              { value: "Cabinet", label: "Cabinet" },
                              { value: "Clinique", label: "Clinique" },
                            ]
                        ).map(({ value, label }) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm">Prénom du directeur</label>
                    <input
                      value={directeurPrenom}
                      onChange={(e) => setDirecteurPrenom(e.target.value.toUpperCase())}
                      className="w-full h-11 rounded-[10px] border px-3 bg-white"
                      style={{ borderColor: "#E5E7EB", textTransform: "uppercase" }}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm">Adresse</label>
                    <input
                      value={adresse}
                      onChange={(e) => setAdresse(e.target.value.toUpperCase())}
                      placeholder="QUARTIER, RUE, VILLE…"
                      className="w-full h-11 rounded-[10px] border px-3 bg-white"
                      style={{ borderColor: "#E5E7EB", textTransform: "uppercase" }}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm">Email du compte établissement</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value.replace(/\s/g, ""))}
                      className="w-full h-11 rounded-[10px] border px-3 bg-white"
                      style={{ borderColor: emailValid || email === "" ? "#E5E7EB" : "#F87171" }}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm">Téléphone <span className="text-gray-400">(optionnel)</span></label>
                    <CountryPhoneInput
                      dialCode={dialCode}
                      national={nationalPhone}
                      onDialCodeChange={setDialCode}
                      onNationalChange={setNationalPhone}
                      invalid={nationalPhone.length > 0 && !phoneValid}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm">Mot de passe</label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full h-11 rounded-[10px] border px-3 bg-white"
                      style={{ borderColor: passwordStrong || password === "" ? "#E5E7EB" : "#F87171" }}
                    />
                    <div className="text-xs text-gray-500">
                      Au moins 8 caractères dont 1 majuscule, 1 minuscule, 1 chiffre et 1 caractère spécial.
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm">N° d'identification / Agrément <span className="text-gray-400">(optionnel)</span></label>
                    <input
                      value={numeroAgrement}
                      onChange={(e) => setNumeroAgrement(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                      placeholder="9707ZZ608E62E8"
                      className="w-full h-11 rounded-[10px] border px-3 bg-white"
                      style={{ borderColor: "#E5E7EB" }}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm">Confirmer le mot de passe</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full h-11 rounded-[10px] border px-3 bg-white"
                      style={{ borderColor: passwordsMatch || confirmPassword === "" ? "#E5E7EB" : "#F87171" }}
                    />
                  </div>
                </div>

                {pendingEtablissementId ? (
                  <div className="space-y-2">
                    <div className="rounded-[8px] border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                      {error}
                    </div>
                    <button
                      type="button"
                      onClick={() => void retryDeclare()}
                      disabled={submitting}
                      className="w-full h-11 rounded-[10px] font-semibold text-white disabled:opacity-60"
                      style={{ background: "#00553E" }}
                    >
                      {submitting ? "Nouvel essai…" : "Réessayer la déclaration d'exercice"}
                    </button>
                  </div>
                ) : (
                  <button
                    type="submit"
                    disabled={!canSubmit}
                    className="w-full h-11 rounded-[10px] font-semibold text-white disabled:opacity-60"
                    style={{ background: "#00553E" }}
                  >
                    {submitting ? "Création…" : "Créer l'établissement"}
                  </button>
                )}
              </form>
            </>
          )}

          {step === "success" && result && (
            <div className="space-y-4 max-w-md mx-auto">
              <div className="rounded-[10px] border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                Établissement « {result.nom} » créé ({result.etablissementId}), en attente de validation. Votre
                déclaration d'exercice a été envoyée — elle aussi en attente.
              </div>

              <div className="space-y-1">
                <label className="text-sm">Email du compte établissement</label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 rounded-[10px] border px-3 py-2 text-sm text-[#0B1F16]" style={{ borderColor: "#E5E7EB" }}>
                    {result.email}
                  </div>
                  <button
                    type="button"
                    onClick={() => void handleCopyEmail()}
                    className="h-10 w-10 shrink-0 inline-flex items-center justify-center rounded-[10px] border"
                    style={{ borderColor: "#00553E", color: "#00553E" }}
                    aria-label="Copier l'email"
                  >
                    {emailCopied ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
              </div>

              <div className="rounded-[10px] border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900 space-y-2">
                <p className="font-semibold">Deux étapes restent à faire depuis une session établissement :</p>
                <ol className="list-decimal pl-4 space-y-1">
                  <li>Soumettre les documents de vérification d'identité de cet établissement.</li>
                  <li>Approuver votre propre demande d'entrée (déclaration d'exercice) dans « Gestion du personnel ».</li>
                </ol>
                <p>
                  Connectez-vous avec l'email et le mot de passe choisis ci-dessus — ce sont deux comptes distincts,
                  votre session professionnelle actuelle ne suffit pas pour ces étapes.
                </p>
              </div>

              <Link
                to="/etablissement/connexion"
                className="flex items-center justify-center gap-2 h-11 rounded-[10px] font-semibold text-white"
                style={{ background: "#00553E" }}
              >
                Se connecter à mon nouvel établissement <ExternalLink size={16} />
              </Link>

              <button
                type="button"
                onClick={handleClose}
                className="w-full h-11 rounded-[10px] font-semibold border"
                style={{ borderColor: "#00553E", color: "#00553E", background: "white" }}
              >
                Plus tard
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
